<script setup lang="ts">
import { CLIENT_API_SUBSCRIBE_EMAIL_URL } from '~/shared/api/client/email/email.const';
import type { IEmailSubscribeClientDTO } from '~/shared/api/client/email/email.dto';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { isValidEmail } from '~/shared/utils/isValidEmail';

const email = ref('');
const isError = ref(false);
const isLoading = ref(false);
const isSubscribed = ref(false);
const alreadyExistsMsg = 'email с таким email уже существует.';

const subscribe = async () => {
  isError.value = false;

  if (email.value === '' || !isValidEmail(email.value)) {
    isError.value = true;
    return;
  }

  isLoading.value = true;
  const { data, error } = await useCustomFetch<IEmailSubscribeClientDTO>(
    CLIENT_API_SUBSCRIBE_EMAIL_URL,
    {
      method: 'POST',
      body: {
        email: email.value,
        status: 'active',
      },
    },
  );

  const err = error.value as {
    data?: { email?: string[] };
  } | null;

  const emailErrors = err?.data?.email;
  const isAlreadyExists =
    Array.isArray(emailErrors) && emailErrors.includes(alreadyExistsMsg);

  const isSuccess = data.value?.id && data.value.status === 'active';

  if (isSuccess || isAlreadyExists) {
    isSubscribed.value = true;
    email.value = '';
  } else {
    isError.value = true;
  }
  isLoading.value = false;
};
</script>

<template>
  <div :class="$style.subscribe">
    <div :class="[$style.subscribe__group, $style.subscribe__group_icon]">
      <div :class="$style.subscribe__iconWrapper">
        <Icon :class="$style.subscribe__icon" name="my-icon:i-mail" />
      </div>
      <div :class="$style.subscribe__title">
        Подпишитесь на эксклюзивные новости CRE
      </div>
    </div>
    <div
      v-if="isSubscribed"
      :class="[
        $style.subscribe__group,
        $style.subscribe__group_form,
        $style.subscribe__subscribeResult,
      ]"
    >
      Спасибо за подписку!
    </div>
    <div
      v-else
      :class="[$style.subscribe__group, $style.subscribe__group_form]"
    >
      <Input
        v-model="email"
        placeholder="Электронная почта"
        type="text"
        :class="$style.subscribe__input"
        :error="isError"
        :disabled="isLoading"
        @click="isError = false"
      />
      <Button
        size="xl"
        theme="primary"
        :is-loading="isLoading"
        @click="subscribe"
      >
        Подписаться
      </Button>
    </div>
  </div>
</template>

<style module lang="scss">
.subscribe {
  display: flex;
  flex-flow: row nowrap;
  gap: var(--space-2-5x);
  padding: var(--space-5x) var(--space-2-5x);
  justify-content: space-between;

  &__group {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: center;
    flex: 1 1 auto;

    &_icon {
      gap: var(--space-2-5x);
    }

    &_form {
      gap: var(--space-1x);
      height: 60px;
      justify-content: center;
    }
  }

  &__iconWrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 90px;
    height: 60px;
    background: var(--color-gray-bg);
  }

  &__icon {
    width: 32px;
    path {
      fill: var(--color-red-1);
    }
  }

  &__title {
    @include text_h5;
    color: var(--color-black-1);
  }

  &__input {
    width: 100%;
    height: 100%;
    display: flex;
    flex-grow: 1;
  }

  &__subscribeResult {
    justify-content: flex-start;
    padding-left: var(--space-2x);
    @include text_h5;
    color: var(--color-black-1);
  }
}

@media (min-width: 1380px) {
  .subscribe {
    &__group {
      &_form {
        width: 520px;
      }
    }
  }
}
@media (min-width: 0px) and (max-width: 1379px) {
  .subscribe {
    flex-direction: column;

    &__group {
      &_icon {
        justify-content: flex-start;
        align-items: center;
      }
    }

    &__title {
      text-align: center;
    }
  }
}

@media (min-width: 0px) and (max-width: 767px) {
  .subscribe {
    &__group {
      &_icon {
        flex-direction: column;
      }
    }
  }
}

@media (max-width: 479px) {
  .subscribe {
    padding: var(--space-2-5x) var(--space-2x);
    &__group {
      &_icon {
        flex-direction: column;
      }

      &_form {
        flex-direction: column;
        height: initial;
      }
    }

    &__input {
      height: 60px;
    }
  }
}
</style>
